import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import IntegrationHero from '../../components/integration-hero'
import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import { getCanonicalUrl } from '../../utils/canonical'
import DarkNav from '../../components/dark-nav'
import './auth0.css'

const Auth0Integration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    // Scroll to top functionality
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)

      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const trackEvent = (eventName, properties = {}) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Auth0' }
  ]

  const heroContent = {
    title: "Auth0 SMS Integration",
    subtitle: "Official Auth0 Marketplace Partner — MFA & Passwordless SMS",
    description: "Add SMS-based multi-factor authentication and passwordless login to your Auth0 applications. Available as an official integration on the Auth0 Marketplace — install in one click and start sending verification codes through DataFlows' Australian SMS network.",
    primaryCta: {
      text: "Install from Auth0 Marketplace",
      href: "https://marketplace.auth0.com/integrations/dataflows-sms",
      onClick: () => trackEvent('auth0_marketplace_clicked')
    },
    secondaryCta: {
      text: "Get API Key",
      href: "https://sms.dataflows.com.au/register"
    }
  }

  const features = [
    {
      icon: "🔐",
      title: "Multi-Factor Authentication",
      description: "Add SMS-based MFA to your Auth0 authentication flow. Send one-time passwords via SMS as a second factor, providing an additional layer of security for user accounts and sensitive operations."
    },
    {
      icon: "📱",
      title: "Passwordless SMS Login",
      description: "Enable passwordless authentication where users sign in using only their phone number. Users receive a one-time code via SMS, eliminating password fatigue and improving the user experience."
    },
    {
      icon: "⚡",
      title: "Auth0 Actions Integration",
      description: "Deploy as a custom Auth0 Action in the 'Send Phone Message' flow. Seamlessly intercepts SMS requests and routes them through DataFlows API with zero changes to your existing authentication logic."
    },
    {
      icon: "💰",
      title: "Cost-Effective SMS Delivery",
      description: "Replace expensive default SMS providers with DataFlows' competitive pricing. Send Australian SMS from just 3.5¢ per message—significantly cheaper than Twilio and other enterprise providers."
    },
    {
      icon: "🌏",
      title: "Australian Infrastructure",
      description: "Optimized for Australian businesses with local SMS routing, faster delivery times, and competitive rates for domestic messages. Also supports international SMS to 200+ countries."
    },
    {
      icon: "📝",
      title: "Custom Message Templates",
      description: "Personalize your MFA and passwordless messages with custom templates. Use the {{code}} placeholder to insert verification codes into branded messages that match your application's voice."
    }
  ]

  const useCases = [
    {
      icon: "🏢",
      title: "Enterprise Applications",
      description: "Secure corporate applications with SMS-based MFA. Protect employee accounts, admin dashboards, and sensitive business systems with an additional authentication factor.",
      keywords: ["enterprise MFA", "corporate security", "admin protection"]
    },
    {
      icon: "🛒",
      title: "E-Commerce Platforms",
      description: "Reduce account takeover fraud in online stores. Verify high-value transactions, protect customer accounts, and build trust with SMS-based security measures.",
      keywords: ["ecommerce security", "fraud prevention", "transaction verification"]
    },
    {
      icon: "🏥",
      title: "Healthcare & Finance",
      description: "Meet compliance requirements for sensitive industries. SMS MFA helps satisfy regulatory standards for protecting patient data, financial information, and personal records.",
      keywords: ["HIPAA compliance", "financial security", "regulated industries"]
    },
    {
      icon: "📲",
      title: "Mobile-First Applications",
      description: "Provide frictionless authentication for mobile users. Passwordless SMS login eliminates typing passwords on small screens, improving conversion rates and user satisfaction.",
      keywords: ["mobile authentication", "passwordless login", "user experience"]
    }
  ]

  const setupSteps = [
    {
      step: 1,
      title: "Get DataFlows Credentials",
      description: "Sign up for a DataFlows account and retrieve your API Token and Sender ID from the Developer Settings page.",
      code: null
    },
    {
      step: 2,
      title: "Install from Auth0 Marketplace",
      description: "Go to the Auth0 Marketplace listing for DataFlows SMS and click 'Add Integration'. Auth0 will automatically create the Action in your tenant's Send Phone Message flow.",
      code: null
    },
    {
      step: 3,
      title: "Configure the Action",
      description: "Open the installed Action in your Auth0 Dashboard. The code is pre-loaded — you only need to add your DataFlows credentials as secrets.",
      code: `// dataflows-sms-action.js
exports.onExecuteSendPhoneMessage = async (event, api) => {
  const response = await fetch('https://api.dataflows.com.au/sms/send', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${event.secrets.DATAFLOWS_API_KEY}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      recipient: event.message_options.recipient,
      sender_id: event.secrets.DATAFLOWS_SENDER_ID,
      message: event.secrets.MESSAGE_TEMPLATE?.replace('{{code}}', event.message_options.text)
               || \`Your verification code is: \${event.message_options.text}\`
    })
  });

  if (!response.ok) {
    throw new Error('SMS delivery failed');
  }
};`
    },
    {
      step: 4,
      title: "Add Secrets",
      description: "In the Action editor, add your DataFlows credentials as secrets for secure storage.",
      code: `DATAFLOWS_API_KEY=your_api_key_here
DATAFLOWS_SENDER_ID=your_sender_id
MESSAGE_TEMPLATE=Your {{app_name}} code is: {{code}}`
    },
    {
      step: 5,
      title: "Deploy & Activate",
      description: "Click 'Deploy' to publish the action, then drag it into the Send Phone Message flow to activate it.",
      code: null
    },
    {
      step: 6,
      title: "Enable Phone MFA",
      description: "Go to Security → Multi-factor Auth → Phone Message. Enable it and select 'Custom' as the delivery provider.",
      code: null
    }
  ]

  const codeExamples = {
    actionCode: `// Auth0 Action: DataFlows SMS Provider
exports.onExecuteSendPhoneMessage = async (event, api) => {
  const { recipient, text } = event.message_options;

  // Build message with custom template or default
  const template = event.secrets.MESSAGE_TEMPLATE || 'Your code is: {{code}}';
  const message = template.replace('{{code}}', text);

  // Send via DataFlows API
  const response = await fetch('https://api.dataflows.com.au/sms/send', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${event.secrets.DATAFLOWS_API_KEY}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      recipient: recipient,
      sender_id: event.secrets.DATAFLOWS_SENDER_ID,
      message: message
    })
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(\`SMS failed: \${error}\`);
  }

  console.log('SMS sent successfully to', recipient);
};`,
    universalLogin: `// Enable MFA in your Auth0 Universal Login
// In your Auth0 Dashboard:
// 1. Go to Security → Multi-factor Auth
// 2. Enable Phone Message
// 3. Configure as required/optional

// Or enforce via Auth0 Rules/Actions:
exports.onExecutePostLogin = async (event, api) => {
  // Require MFA for admin users
  if (event.user.roles?.includes('admin')) {
    api.multifactor.enable('any', { allowRememberBrowser: true });
  }
};`,
    passwordless: `// Configure Passwordless SMS in Auth0
// Dashboard → Authentication → Passwordless

// Client-side implementation (Auth0 SDK):
import { Auth0Client } from '@auth0/auth0-spa-js';

const auth0 = new Auth0Client({
  domain: 'your-tenant.au.auth0.com',
  clientId: 'your_client_id'
});

// Start passwordless flow
await auth0.passwordlessStart({
  connection: 'sms',
  send: 'code',
  phoneNumber: '+61412345678'
});

// Verify the code
await auth0.passwordlessLogin({
  connection: 'sms',
  phoneNumber: '+61412345678',
  verificationCode: '123456'
});`
  }

  const benefits = [
    "Official Auth0 Marketplace integration — vetted and listed by Auth0 directly",
    "One-click install — no manual code setup, Auth0 handles the deployment",
    "Australian data routing — faster SMS delivery for local users",
    "Transparent pricing — no hidden fees, monthly minimums, or long-term contracts",
    "Enterprise-ready — scales from startups to high-volume enterprise applications"
  ]

  const technicalSpecs = [
    { label: "Runtime", value: "Node.js 18 (Auth0 Actions)" },
    { label: "Auth Flow", value: "Send Phone Message" },
    { label: "Auth Methods", value: "MFA, Passwordless" },
    { label: "API Format", value: "REST / JSON" },
    { label: "Distribution", value: "Auth0 Marketplace" },
    { label: "Support", value: "Officially Supported" }
  ]

  const faqItems = [
    {
      question: "How does the Auth0 SMS Action work?",
      answer: "When a user triggers SMS-based authentication (MFA or passwordless login), Auth0 calls your custom Action in the 'Send Phone Message' flow. The Action receives the phone number and verification code, then sends the SMS through DataFlows API instead of Auth0's default provider."
    },
    {
      question: "Is this a replacement for Twilio in Auth0?",
      answer: "Yes! Auth0's default SMS provider is Twilio, which can be expensive especially for Australian traffic. This integration lets you use DataFlows instead, with significantly lower per-message costs and optimized Australian delivery."
    },
    {
      question: "Can I use custom Sender IDs?",
      answer: "Absolutely. Configure your DATAFLOWS_SENDER_ID secret with your registered business name or virtual number. Your SMS messages will display your brand instead of a random number, improving trust and recognition."
    },
    {
      question: "What Auth0 plans support custom SMS providers?",
      answer: "Auth0 Actions for Send Phone Message are available on all paid Auth0 plans. The free tier has limitations on custom Actions, so check your plan's features in the Auth0 dashboard."
    },
    {
      question: "How do I customize the verification message?",
      answer: "Set the MESSAGE_TEMPLATE secret with your custom text, using {{code}} as a placeholder for the verification code. For example: 'Your MyApp security code is: {{code}}. Valid for 10 minutes.'"
    },
    {
      question: "What happens if SMS delivery fails?",
      answer: "The Action throws an error, which Auth0 handles according to your flow configuration. Users typically see a retry option. DataFlows provides delivery status in your dashboard for troubleshooting."
    },
    {
      question: "Is this secure?",
      answer: "Yes. Your API credentials are stored as Auth0 Secrets, which are encrypted and never exposed to client-side code. The Action runs server-side in Auth0's secure infrastructure."
    },
    {
      question: "Can I use this for both MFA and passwordless?",
      answer: "Yes! The same Action handles both use cases. Auth0 uses the Send Phone Message flow for all SMS-based authentication, whether it's a second factor (MFA) or the primary authentication method (passwordless)."
    }
  ]

  const comparisonData = [
    { feature: "Australian SMS", dataflows: "3.5¢", twilio: "7.5¢+", savings: "53%" },
    { feature: "Monthly Fee", dataflows: "None", twilio: "$0-15+", savings: "100%" },
    { feature: "Setup Complexity", dataflows: "Simple", twilio: "Default", savings: "-" },
    { feature: "Custom Templates", dataflows: "Yes", twilio: "Limited", savings: "-" },
    { feature: "Local Support", dataflows: "Australia", twilio: "Global", savings: "-" }
  ]

  return (
    <div className="auth0-integration-container">
      <DarkNav />
      <Helmet>
        <title>Auth0 SMS Integration — MFA & Passwordless Authentication | DataFlows</title>
        <meta
          name="description"
          content="Official Auth0 Marketplace integration by DataFlows. Add SMS MFA and passwordless login to Auth0 with cost-effective Australian SMS delivery. Install directly from the Auth0 Marketplace."
        />
        <meta name="keywords" content="Auth0 SMS, Auth0 Marketplace, Auth0 MFA, Auth0 passwordless, Auth0 SMS provider, Auth0 Twilio alternative, Auth0 phone authentication, SMS multi-factor authentication, Auth0 Actions SMS, Auth0 OTP, Auth0 verification SMS, Auth0 Australia, passwordless SMS login, Auth0 custom SMS, SMS authentication Auth0, two-factor authentication Auth0, Auth0 phone MFA, Auth0 official integration" />
        <meta property="og:title" content="Auth0 SMS Integration — Official Auth0 Marketplace Partner | DataFlows" />
        <meta property="og:description" content="Official DataFlows SMS integration on the Auth0 Marketplace. Add SMS MFA and passwordless login to Auth0 with Australian SMS delivery." />
        <meta property="og:image" content="https://dataflows.com.au/images/integrations/auth0/og-image.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Auth0 SMS Integration — Send MFA Codes via DataFlows" />
        <meta name="twitter:description" content="Add phone-based authentication to Auth0. Open-source Action for SMS MFA and passwordless login." />
        <link rel="canonical" href={getCanonicalUrl('/integrations/auth0')} />

        {/* Structured data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "DataFlows Auth0 SMS Action",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Cloud",
            "description": "Auth0 Action for sending SMS MFA and passwordless verification codes via DataFlows SMS API",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "AUD"
            },
            "author": {
              "@type": "Organization",
              "name": "DataFlows"
            }
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Auth0", "item": "https://dataflows.com.au/integrations/auth0" }
          ]
        })}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="auth0-integration-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <IntegrationHero {...heroContent} />

      {/* What is Section */}
      <section className="auth0-overview" data-aos="fade-up">
        <div className="container">
          <div className="overview-content">
            <div className="overview-text">
              <h2 className="section-title">What is Auth0 SMS Action?</h2>
              <p className="section-description left-align">
                <strong>DataFlows SMS</strong> is an official integration on the <a href="https://marketplace.auth0.com/integrations/dataflows-sms" target="_blank" rel="noreferrer noopener" className="inline-link">Auth0 Marketplace</a>, enabling SMS-based authentication for your Auth0 applications. It intercepts phone message requests in the Send Phone Message flow and delivers verification codes through DataFlows' SMS API.
              </p>
              <p className="section-description left-align">
                Whether you need multi-factor authentication (MFA) to add a second layer of security, or passwordless login for a frictionless user experience, this integration provides a cost-effective alternative to Auth0's default Twilio provider — with Australian-based support and competitive pricing.
              </p>
              <div className="overview-badges">
                <span className="badge badge-official">Auth0 Marketplace</span>
                <span className="badge">Official Partner</span>
                <span className="badge">Auth0 Actions</span>
                <span className="badge">MFA Ready</span>
              </div>
            </div>
            <div className="overview-graphic" data-aos="fade-left">
              <div className="flow-diagram">
                <div className="flow-step">
                  <div className="flow-icon">👤</div>
                  <div className="flow-label">User Login</div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="flow-icon">🔐</div>
                  <div className="flow-label">Auth0 MFA</div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="flow-icon">⚡</div>
                  <div className="flow-label">Action</div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="flow-icon">📱</div>
                  <div className="flow-label">SMS Sent</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="auth0-features" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Features & Capabilities</h2>
            <p className="section-description">
              Everything you need for SMS-based authentication in Auth0
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="auth0-use-cases" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Use Cases for Auth0 SMS</h2>
            <p className="section-description">
              Common scenarios where SMS authentication enhances security
            </p>
          </div>

          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="use-case-icon">{useCase.icon}</div>
                <h3 className="use-case-title">{useCase.title}</h3>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-keywords">
                  {useCase.keywords.map((keyword, i) => (
                    <span key={i} className="keyword-tag">{keyword}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Guide */}
      <section className="auth0-setup" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Step-by-Step Setup Guide</h2>
            <p className="section-description">
              Deploy the Auth0 SMS Action in 6 simple steps
            </p>
          </div>

          <div className="setup-steps">
            {setupSteps.map((step, index) => (
              <div key={step.step} className="setup-step" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="step-header">
                  <div className="step-number">{step.step}</div>
                  <div className="step-content">
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-description">{step.description}</p>
                  </div>
                </div>
                {step.code && (
                  <div className="step-code">
                    <pre><code>{step.code}</code></pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="setup-cta">
            <a
              href="https://marketplace.auth0.com/integrations/dataflows-sms"
              target="_blank"
              rel="noreferrer noopener"
              className="cta-button primary"
              onClick={() => trackEvent('auth0_marketplace_clicked', { source: 'setup-guide' })}
            >
              Install from Auth0 Marketplace
            </a>
            <p className="setup-guide-link">
              Prefer a full walkthrough with best practices?{' '}
              <Link to="/blog/sms-mfa-auth0-dataflows-setup" className="inline-link">
                Read our step-by-step Auth0 SMS MFA setup guide
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="auth0-code-examples" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Code Examples</h2>
            <p className="section-description">
              Implementation patterns for Auth0 SMS authentication
            </p>
          </div>

          <div className="code-examples-grid">
            <div className="code-example" data-aos="fade-up">
              <h3 className="code-example-title">Auth0 Action Code</h3>
              <p className="code-example-description">The complete Action code for sending SMS via DataFlows</p>
              <div className="code-block">
                <pre><code>{codeExamples.actionCode}</code></pre>
              </div>
            </div>

            <div className="code-example" data-aos="fade-up">
              <h3 className="code-example-title">Enforce MFA via Actions</h3>
              <p className="code-example-description">Require MFA for specific users or conditions</p>
              <div className="code-block">
                <pre><code>{codeExamples.universalLogin}</code></pre>
              </div>
            </div>

            <div className="code-example full-width" data-aos="fade-up">
              <h3 className="code-example-title">Passwordless SMS Login</h3>
              <p className="code-example-description">Client-side implementation for passwordless authentication</p>
              <div className="code-block">
                <pre><code>{codeExamples.passwordless}</code></pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="auth0-comparison" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">DataFlows vs Default Auth0 SMS</h2>
            <p className="section-description">
              Why developers choose DataFlows for Auth0 SMS
            </p>
          </div>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="highlight">DataFlows</th>
                  <th>Twilio (Default)</th>
                  <th>Savings</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.feature}</td>
                    <td className="highlight">{row.dataflows}</td>
                    <td>{row.twilio}</td>
                    <td className="savings">{row.savings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="auth0-benefits" data-aos="fade-up">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2 className="section-title">Why Use This Integration?</h2>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={index} className="benefit-item" data-aos="fade-up" data-aos-delay={index * 100}>
                    <svg className="benefit-check" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="12" fill="#EB5424" fillOpacity="0.1"/>
                      <path d="M17.707 8.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L10 14.586l6.293-6.293a1 1 0 011.414 0z" fill="#EB5424"/>
                    </svg>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="benefits-specs" data-aos="fade-left">
              <h3 className="specs-title">Technical Specifications</h3>
              <div className="specs-grid">
                {technicalSpecs.map((spec, index) => (
                  <div key={index} className="spec-item">
                    <span className="spec-label">{spec.label}</span>
                    <span className="spec-value">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="auth0-pricing" data-aos="fade-up">
        <div className="container">
          <div className="pricing-content">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="pricing-subtitle">The Action is free. You only pay for SMS sent.</p>
            <div className="pricing-grid">
              <div className="pricing-item">
                <div className="price">Free</div>
                <div className="price-label">Auth0 Action</div>
                <div className="price-note">Open-source code</div>
              </div>
              <div className="pricing-item featured">
                <div className="price">from 2.5¢</div>
                <div className="price-label">Per SMS (Australia)</div>
                <div className="price-note">on Business Plan</div>
              </div>
              <div className="pricing-item">
                <div className="price">Free</div>
                <div className="price-label">Inbound SMS</div>
                <div className="price-note">Replies are free</div>
              </div>
            </div>
            <div className="pricing-cta">
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('signup_cta_clicked', { source: 'auth0-pricing' })}
              >
                Get Free API Key
              </a>
              <a
                href="/contact-us"
                className="cta-button secondary"
                onClick={() => trackEvent('contact_support_clicked', { integration: 'auth0' })}
              >
                Enterprise Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="auth0-faq" data-aos="fade-up">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item" data-aos="fade-up" data-aos-delay={index * 50}>
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Keywords Section (for SEO) */}
      <section className="auth0-keywords" data-aos="fade-up">
        <div className="container">
          <h2 className="section-title">Related Topics</h2>
          <div className="keywords-cloud">
            <Link to="/integrations/supabase" className="keyword-link">Supabase SMS Integration</Link>
            <Link to="/integrations/zapier" className="keyword-link">Zapier SMS Automation</Link>
            <span className="keyword-link">Auth0 Authentication</span>
            <Link to="/blog/sms-mfa-auth0-dataflows-setup" className="keyword-link">Auth0 SMS MFA Setup Guide</Link>
            <span className="keyword-link">Passwordless Login</span>
            <span className="keyword-link">SMS API Australia</span>
            <span className="keyword-link">Identity Management</span>
            <span className="keyword-link">Twilio Alternative</span>
            <span className="keyword-link">SMS Verification</span>
            <span className="keyword-link">Phone Authentication</span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="auth0-final-cta">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <h2 className="cta-title">Ready to Add SMS Authentication to Auth0?</h2>
            <p className="cta-description">
              Install the official DataFlows integration directly from the Auth0 Marketplace and start sending secure verification codes in minutes.
            </p>
            <div className="cta-buttons">
              <a
                href="https://marketplace.auth0.com/integrations/dataflows-sms"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('auth0_marketplace_clicked', { source: 'final-cta' })}
              >
                Install from Auth0 Marketplace
              </a>
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button secondary"
                onClick={() => trackEvent('signup_cta_clicked', { source: 'final-cta' })}
              >
                Get API Key
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll to Top Button */}
      <div
        className={`scroll-to-top ${showScrollTop ? 'show' : ''}`}
        onClick={scrollToTop}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/>
        </svg>
      </div>
    </div>
  )
}

export default Auth0Integration
